/**
 * Flip: the same grid read the other way round.
 *
 * Two reports used to do this by writing the table TWICE — a `<table>` per
 * axis, each a transpose of the other, so a change to a cell had to be made in
 * both and a change to only one was invisible until somebody flipped. The
 * feature matrix carried two full bodies; the spec sheet swapped its loops a
 * third time.
 *
 * A grid is columns × rows, and a transpose is not a rendering decision — it
 * is arithmetic on the column list. So it is done once, here, and every table
 * can flip whether or not anybody had thought to write the other branch.
 *
 * THE FIRST COLUMN NAMES THE ROW. Going down it holds the row's name; flipped,
 * it holds the names of what used to be the other columns. Everything after it
 * is a FIELD of the row, and each becomes a row of its own.
 *
 * What survives the flip, because it is written as a function of the row
 * rather than of the position:
 *   - `cellId`, so `cell-<feature>-<product>` is that cell either way round
 *   - `cell`, so a cell is drawn by the same code in both orientations
 *   - `value`, so the file and the sort read what they always read
 *
 * What does not: sections and row detail. A band across a set of rows has no
 * meaning once those rows are columns, and neither has a detail row hanging
 * under one. The tables that had them never drew them flipped either.
 */
import type { Column } from './tableColumns';

/** A flipped row IS one of the original columns. Naming it makes the two
 *  generic parameters below readable — `Column<Field<T>>` is a column of the
 *  flipped table, whose rows are the fields of the original. */
export type Field<T> = Column<T>;

export interface Flipped<T> {
  columns: Column<Field<T>>[];
  rows: Field<T>[];
}

/** The first column is the one that names the row; the rest are its fields. */
export function headerColumn<T>(columns: Column<T>[]): Column<T> | undefined {
  return columns[0];
}

/**
 * Columns × rows becomes rows × columns.
 *
 * `rowKey` names the new columns, so the picker's memory of a hidden column
 * survives a reload the same way it does unflipped.
 */
export function transpose<T>(
  columns: Column<T>[],
  rows: T[],
  rowKey: (row: T) => string | number,
): Flipped<T> {
  const header = headerColumn(columns);
  const fields = columns.slice(1);

  /* The label column: down its side run the names of the columns that were.
     It is drawn by their own `head` where they have one, so a product column
     headed by a button stays a button once it is a row. */
  const labels: Column<Field<T>> = {
    key: '__field',
    label: header?.flipLabel ?? '',
    /* It carries the name it came from, so flipping back is flipping back
       rather than a one-way trip that forgets what the column was called. */
    flipLabel: header?.label,
    width: header?.width,
    align: header?.align,
    always: true,
    value: f => f.label,
    cell: f => f.head ?? f.label,
  };

  /* One column per row: it is that row's name at the top, and that row's
     value under it. `head` carries the name the row was drawn with — the
     button, the badge — while `label` stays the plain string the picker and
     the file want. */
  const entities: Column<Field<T>>[] = rows.map(row => ({
    key: String(rowKey(row)),
    label: header ? String(header.value(row) ?? '') : String(rowKey(row)),
    head: header?.cell?.(row),
    width: header?.rowWidth?.(row),
    value: f => f.value(row),
    cell: f => (f.cell ? f.cell(row) : (f.value(row) ?? '')),
    cellId: f => f.cellId?.(row) ?? '',
    cellClass: f => f.cellClass?.(row) ?? '',
    /* Alignment described the field, so it follows the field into its row:
       every cell of a price row reads right-aligned, in either orientation. */
    cellAlign: f => f.align,
  }));

  return { columns: [labels, ...entities], rows: fields };
}

// Remembered per table: which way round you last read this one.

const FLIP_KEY = 'jcseo.boardroom.flip';

export function storedFlip(table: string, fallback = false): boolean {
  try {
    const raw = window.localStorage.getItem(`${FLIP_KEY}.${table}`);
    return raw === null ? fallback : raw === '1';
  } catch {
    return fallback;
  }
}

export function rememberFlip(table: string, on: boolean): void {
  try {
    window.localStorage.setItem(`${FLIP_KEY}.${table}`, on ? '1' : '0');
  } catch {
    /* private mode: the default is fine */
  }
}

/** Flipping a LIST makes a column per row, and four hundred columns is not a
 *  reading of anything — so an ordinary bounded table offers it only below the
 *  cap.
 *
 *  A GRID is the other case entirely. A table with a column per product is
 *  already wide and already scrolling sideways; turning it is not a novelty
 *  there, it is the reading the table exists for, and it does not stop being
 *  so at the thirty-first feature. Hiding the control on the biggest matrix in
 *  the room takes it away from exactly the table that needed it.
 *
 *  Always offered once flipped, either way, or there would be no way back. */
export function canFlip(rowCount: number, max: number, flipped: boolean, grid = false): boolean {
  if (max <= 0) return false;
  return flipped || grid || rowCount <= max;
}
